import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { Formik, ErrorMessage } from 'formik';
import { FormGroup, Label, Form, FormFeedback, Tooltip, Input } from 'reactstrap';

import PrimaryButton from '../PrimaryButton';
import '../FormComponents/styles.scss';

import EyeIcon from '../../assets/eye.svg';
import EyeCrossedIcon from '../../assets/eye-crossed.svg';
import { useStore } from '../../store';

const CustomFormFeedback = ({ children }) => <FormFeedback className="styled-form-feedback">{children}</FormFeedback>;

const PasswordForm = observer(() => {
  const {
    rootStore: { profileStore },
  } = useStore();

  const [passwordFocus, setPasswordFocus] = useState(false);
  const [show, setShow] = useState({
    currentPassword: false,
    newPassword: false,
    repeatNewPassword: false,
  });

  const { t } = useTranslation();

  const changePasswordFocus = isFocused => {
    setPasswordFocus(isFocused);
  };

  const toggleShowPassword = type => {
    setShow(prevState => ({
      ...prevState,
      [type]: !prevState[type],
    }));
  };

  let initialValues = {
    currentPassword: '',
    newPassword: '',
    newPasswordConfirm: '',
  };

  return (
    <>
      <Formik
        initialValues={initialValues}
        touchOnChange
        validate={values => {
          return profileStore.passwordStore.validatePassword(values, t);
        }}
        onSubmit={(values, { setSubmitting, resetForm }) => {
          profileStore.passwordStore.savePasswordChange(values, t).then(() => {
            setSubmitting(false);
            resetForm({});
          });
        }}
      >
        {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting }) => (
          <Form className="styled-form" onSubmit={handleSubmit}>
            <FormGroup>
              <span className="has-float-label">
                <Input
                  className="styled-inputs"
                  type={show.currentPassword ? 'text' : 'password'}
                  name="currentPassword"
                  id="password-current"
                  autoComplete="current-password"
                  placeholder={t('currentPassword.placeholder')}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.currentPassword}
                  maxLength={255}
                  invalid={!!errors.currentPassword && touched.currentPassword}
                />
                {values.currentPassword.length ? (
                  <span className="styled-input-icon" onClick={() => toggleShowPassword('currentPassword')}>
                    <img
                      className="styled-eye-icon"
                      src={show.currentPassword ? EyeIcon : EyeCrossedIcon}
                      alt="eye-icon"
                    />
                  </span>
                ) : null}
                <Label
                  for="password-current"
                  className={!!errors.currentPassword && touched.currentPassword ? 'is-invalid' : ''}
                >
                  {t('currentPassword.label')}
                </Label>
                <ErrorMessage name="currentPassword" component={CustomFormFeedback} />
              </span>
            </FormGroup>
            <FormGroup>
              <span className="has-float-label">
                <Input
                  className="styled-inputs"
                  type={show.newPassword ? 'text' : 'password'}
                  name="newPassword"
                  autoComplete="new-password"
                  id="password-new"
                  placeholder={t('newPassword.placeholder')}
                  onChange={handleChange}
                  onBlur={e => {
                    handleBlur(e);
                    changePasswordFocus(false);
                  }}
                  onFocus={() => {
                    changePasswordFocus(true);
                  }}
                  value={values.newPassword}
                  invalid={!!errors.newPassword && touched.newPassword}
                  maxLength={255}
                />
                {values.newPassword.length ? (
                  <span className="styled-input-icon" InputIcon onClick={() => toggleShowPassword('newPassword')}>
                    <img className="styled-eye-icon" src={show.newPassword ? EyeIcon : EyeCrossedIcon} alt="eye-icon" />
                  </span>
                ) : null}
                <Label for="password-new" className={!!errors.newPassword && touched.newPassword ? 'is-invalid' : ''}>
                  {t('newPassword.label')}
                </Label>
                <ErrorMessage name="newPassword" component={CustomFormFeedback} />
                <Tooltip
                  className="styled-tooltip"
                  target="password-new"
                  position="top"
                  isOpen={
                    passwordFocus &&
                    !!errors.newPassword &&
                    Object.keys(profileStore.passwordStore.newPasswordErrors).length > 0
                  }
                >
                  <span className={profileStore.passwordStore.newPasswordErrors.length ? 'error' : 'ok'}>
                    {t('passwordStrength.length')}
                  </span>{' '}
                  <br />
                  <span className={profileStore.passwordStore.newPasswordErrors.strength ? 'error' : 'ok'}>
                    {t('passwordStrength.shouldContain')}
                  </span>
                  <br />
                  <span className={profileStore.passwordStore.newPasswordErrors.numbers ? 'inner' : 'ok inner'}>
                    {t('passwordStrength.numbers')}
                  </span>
                  <br />
                  <span
                    className={profileStore.passwordStore.newPasswordErrors.specialCharacters ? 'inner' : 'ok inner'}
                  >
                    {t('passwordStrength.specialCharacters')}
                  </span>
                  <br />
                  <span className={profileStore.passwordStore.newPasswordErrors.lowerCase ? 'inner' : 'ok inner'}>
                    {t('passwordStrength.lowerCase')}
                  </span>
                  <br />
                  <span className={profileStore.passwordStore.newPasswordErrors.upperCase ? '' : 'ok'}>
                    &nbsp;&nbsp;{t('passwordStrength.upperCase')}
                  </span>
                  <br />
                </Tooltip>
              </span>
            </FormGroup>
            <FormGroup>
              <span className="has-float-label">
                <Input
                  className="styled-inputs"
                  type={show.repeatNewPassword ? 'text' : 'password'}
                  name="newPasswordConfirm"
                  autoComplete="new-password"
                  id="password-new-confirm"
                  placeholder={t('newPasswordConfirm.placeholder')}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.newPasswordConfirm}
                  invalid={!!errors.newPasswordConfirm && touched.newPasswordConfirm}
                  maxLength={255}
                />
                {values.newPasswordConfirm.length ? (
                  <span className="styled-input-icon" onClick={() => toggleShowPassword('repeatNewPassword')}>
                    <img
                      className="styled-eye-icon"
                      src={show.repeatNewPassword ? EyeIcon : EyeCrossedIcon}
                      alt="eye-icon"
                    />
                  </span>
                ) : null}

                <Label
                  for="password-new-confirm"
                  className={!!errors.newPasswordConfirm && touched.newPasswordConfirm ? 'is-invalid' : ''}
                >
                  {t('newPasswordConfirm.label')}
                </Label>
                <ErrorMessage name="newPasswordConfirm" component={CustomFormFeedback} />
              </span>
            </FormGroup>

            <PrimaryButton
              type="submit"
              disabled={isSubmitting || Object.keys(errors).length !== 0 || Object.keys(touched).length === 0}
            >
              {t('saveButton')}
            </PrimaryButton>
          </Form>
        )}
      </Formik>
    </>
  );
});

export default PasswordForm;
